// Real content from https://ayushjha1.myportfolio.com/ + resume PDF
// Designer: Deepanshu Kumar Jha — Graphic Designer & Community Manager
// "Ayush" is a nickname/alias used only in the hero signature.
// Images are served from /portfolio/* (downloaded locally for fast CDN delivery).

export const personal = {
  firstName: "Deepanshu",
  alias: "Ayush",
  lastName: "Jha",
  fullName: "Deepanshu Jha",
  signatureName: 'Deepanshu "Ayush" Jha', // only used in the hero signature card
  tagline: "Graphic Designer & Community Manager",
  intro:
    "Crafting visual stories for creators, brands and communities — from scroll-stopping YouTube thumbnails to high-converting social campaigns and Discord communities that actually move.",
  shortBio:
    "I'm Deepanshu — a graphic designer and Discord community manager with 2+ years scaling creator communities to 90,000+ members and producing 50+ YouTube and social assets that move CTR and engagement. I build the systems behind the visuals — moderation workflows, bot setups, content pipelines — so brands run without daily hand-holding.",
  email: "workwithayush858@gmail.com",
  phone: "+91 91515 12301",
  resume: "https://customer-assets.emergentagent.com/job_portfolio-pro-1838/artifacts/ym2r89ap_Deepanshu_Kumar_Jha_-_Graphic_Designer__Community_Manager.pdf",
  linkedin: "https://www.linkedin.com/in/deepanshu-k-8a536a402/",
  instagram: "https://www.instagram.com/ayushjha_ig/",
  twitter: "https://x.com/ayushjha_x",
  behance: "https://www.behance.net/ayushportfolio",
  location: "Prayagraj, India · Working remotely",
  yearsActive: "2023 — Present",
};

export const services = [
  {
    no: "01",
    title: "YouTube Thumbnails",
    desc: "High-CTR thumbnails engineered for the algorithm — bold typography, sharp contrast, emotion-first faces.",
    deliverables: ["A/B variants", "Series templating", "Channel art"],
  },
  {
    no: "02",
    title: "Social Media Creatives",
    desc: "Instagram posts, carousels, banners and promo graphics that hold attention beyond the first frame.",
    deliverables: ["Static & carousel", "Story sets", "Brand systems"],
  },
  {
    no: "03",
    title: "Marketing Campaigns",
    desc: "Multi-creative campaigns tied to clear CTAs — built around brand voice and tested for conversion.",
    deliverables: ["Campaign kits", "Brochures", "Landing creatives"],
  },
  {
    no: "04",
    title: "Community Management",
    desc: "Discord & social communities — moderation, bots and growth playbooks.",
    deliverables: ["Discord setup", "Moderation team", "Growth ops"],
  },
];

// Experience timeline from resume — shown in About section
export const experience = [
  {
    role: "Graphic Designer Intern",
    company: "Exophian.AI",
    dates: "Jun 2026 — Present",
    type: "Internship",
  },
  {
    role: "Community Lead",
    company: "Klurge",
    dates: "Jan 2025 — Present",
    type: "Part-time",
  },
  {
    role: "Freelance Graphic Designer",
    company: "Self-employed",
    dates: "Jul 2023 — Present",
    type: "Freelance",
  },
  {
    role: "Online Community Manager",
    company: "Harsh Khelraay",
    dates: "Nov 2023 — Mar 2026",
    type: "90K+ Discord",
  },
  {
    role: "Graphic Designer",
    company: "Daixe0",
    dates: "Jan 2025 — May 2025",
    type: "Gaming creator",
  },
  {
    role: "Graphic Designer",
    company: "Luke Boshard",
    dates: "Jan 2024 — Feb 2024",
    type: "Instagram visuals",
  },
];

const ACCREDIAN_COVER = "/portfolio/covers/accredian-cover.png";
const SOCIAL_COVER    = "/portfolio/covers/social-cover.png";
const YT_COVER        = "/portfolio/covers/yt-cover.png";

const ACCREDIAN_IMGS = [
  "/portfolio/accredian/1.png",
  "/portfolio/accredian/2.png",
  "/portfolio/accredian/3.png",
];

const SOCIAL_IMGS = Array.from({ length: 17 }, (_, i) => `/portfolio/social/${i + 1}.png`);

const YT_IMGS = [
  "/portfolio/youtube/1.png",  "/portfolio/youtube/2.jpg",  "/portfolio/youtube/3.png",
  "/portfolio/youtube/4.png",  "/portfolio/youtube/5.jpg",  "/portfolio/youtube/6.png",
  "/portfolio/youtube/7.png",  "/portfolio/youtube/8.jpg",  "/portfolio/youtube/9.jpg",
  "/portfolio/youtube/10.png", "/portfolio/youtube/11.png", "/portfolio/youtube/12.jpg",
  "/portfolio/youtube/13.png", "/portfolio/youtube/14.png", "/portfolio/youtube/15.png",
  "/portfolio/youtube/16.png", "/portfolio/youtube/17.png", "/portfolio/youtube/18.png",
  "/portfolio/youtube/19.jpg", "/portfolio/youtube/20.png", "/portfolio/youtube/21.jpg",
  "/portfolio/youtube/22.png", "/portfolio/youtube/23.jpg", "/portfolio/youtube/24.jpg",
  "/portfolio/youtube/25.png", "/portfolio/youtube/26.jpg", "/portfolio/youtube/27.jpg",
  "/portfolio/youtube/28.jpg", "/portfolio/youtube/29.jpg", "/portfolio/youtube/30.png",
  "/portfolio/youtube/31.png", "/portfolio/youtube/32.png", "/portfolio/youtube/33.png",
  "/portfolio/youtube/34.png", "/portfolio/youtube/35.png", "/portfolio/youtube/36.png",
  "/portfolio/youtube/37.png",
];

// ─────────── Featured (top works pinned at top of page) ───────────
export const featured = [
  {
    id: "accredian",
    slug: "accredian-educational-marketing-campaign",
    title: "Accredian — Marketing Campaign",
    category: "Marketing Campaign",
    year: "2026",
    role: "Graphic Designer",
    summary:
      "Educational marketing creatives for an 'AI for Beginners' course plus an executive-education brochure redesign.",
    description:
      "Created as part of a Graphic Design Internship assessment for Accredian. Objective: design engaging marketing creatives that hold brand consistency while pushing clear call-to-actions.",
    deliverables: ["2 Social Media Creatives", "1 Brochure Redesign"],
    cover: ACCREDIAN_COVER,
    images: ACCREDIAN_IMGS,
    rawImages: ACCREDIAN_IMGS,
    featured: true,
    span: "large",
    caseStudy: {
      client: "Accredian (EdTech)",
      duration: "Internship Assessment",
      tools: ["Adobe Photoshop"],
      problem:
        "Accredian needed marketing creatives that could compete inside crowded EdTech feeds — clarity around the 'AI for Beginners' course, plus a refreshed brochure for their executive-education program. The old assets were busy, brand-inconsistent and weak on conversion.",
      process: [
        "Audited Accredian's existing brand language (palette, type, voice) and competitor creatives.",
        "Designed two social-media creatives anchored on a single hero device + headline hierarchy — A/B'd two layouts before finalising.",
        "Redesigned the executive-education brochure with a modular grid, restrained type scale and one bold accent colour driving the CTA.",
      ],
      outcome: [
        "Two on-brand social creatives with a clear ENROLL NOW CTA hierarchy.",
        "A brochure layout that reads in 30 seconds yet sells the full program.",
        "Templates the in-house team can extend without breaking brand.",
      ],
    },
  },
  {
    id: "social-media",
    slug: "social-media-creatives",
    title: "Social Media Creatives",
    category: "Social Media",
    year: "2024 – 2026",
    role: "Graphic Designer",
    summary:
      "Instagram posts, banners and promotional graphics built for engagement across multiple brand voices.",
    description:
      "A rolling body of work covering Instagram posts, banners, carousels and promotional graphics. Each piece is tuned for the platform — composition, hierarchy and motion-readiness — so the creative earns attention in the feed.",
    deliverables: ["Instagram Posts", "Banners", "Promotional Graphics"],
    cover: SOCIAL_COVER,
    images: SOCIAL_IMGS,
    rawImages: SOCIAL_IMGS,
    featured: true,
    span: "tall",
    caseStudy: {
      client: "Multiple — creator brands, F&B, gaming, lifestyle",
      duration: "Ongoing · 2024 — Present",
      tools: ["Adobe Photoshop", "AI-assisted workflows"],
      problem:
        "Different brand voices, same problem — feed creative that doesn't earn the second look. Posts were inconsistent, undirected and easy to scroll past.",
      process: [
        "Built per-brand mini-systems (palette + type pair + grid) so every post stays on-brand without feeling repetitive.",
        "Standardised the focal point: each creative leads with ONE message, supported by hierarchy and contrast.",
        "Carousel pacing — first slide hooks, middle slides deliver, last slide converts.",
      ],
      outcome: [
        "30+ creatives delivered across multiple clients within 24-48h SLAs.",
        "Branded post systems that the clients now reuse internally.",
        "Average CTR uplift of ~20% on promoted creatives.",
      ],
    },
  },
  {
    id: "youtube-thumbnails",
    slug: "youtube-thumbnails",
    title: "YouTube Thumbnails",
    category: "YouTube",
    year: "2023 – 2026",
    role: "Graphic Designer",
    summary:
      "Click-worthy thumbnails for gaming, education and entertainment channels — engineered for CTR.",
    description:
      "Thumbnail design across gaming content, educational videos and entertainment channels. Each thumbnail is built around a single attention-hook: face, contrast, headline. The objective is binary — earn the click.",
    deliverables: ["Gaming Thumbnails", "Educational Thumbnails", "Entertainment Thumbnails"],
    cover: YT_COVER,
    images: YT_IMGS,
    rawImages: YT_IMGS,
    featured: true,
    span: "wide",
    caseStudy: {
      client: "Gaming + Education + Entertainment creators (incl. Daixe0)",
      duration: "Ongoing · 2023 — Present",
      tools: ["Adobe Photoshop", "AI-assisted retouching"],
      problem:
        "A thumbnail has 0.5 seconds to win attention. Creators were uploading high-effort videos behind low-effort thumbnails — CTR was leaving views on the table.",
      process: [
        "Decoded each channel's audience trigger — face, weapon, headline, contrast, scarcity.",
        "Built a CTR formula: 1 emotion-first subject + 1 sharp headline + 1 colour conflict + clean negative space.",
        "Templated the per-channel system so creators could ship consistently and still A/B variants.",
      ],
      outcome: [
        "50+ thumbnails shipped across gaming, education and entertainment.",
        "Up to ~20% CTR uplift measured by partnered creators.",
        "24-48h turnaround pipeline that keeps creator upload schedules intact.",
      ],
    },
  },
];

export const projects = featured;
// "All" removed per user request — only category tabs.
export const categories = ["Marketing Campaign", "Social Media", "YouTube"];

export const clients = [
  "Harsh Khelraay",
  "Klurge",
  "Gaming Creators",
  "Education Brands",
  "EdTech",
  "Discord Communities",
  "Lifestyle Brands",
  "Entertainment Channels",
];

export const stats = [
  { value: "90K+", label: "Discord Members Scaled" },
  { value: "50+", label: "Creative Assets" },
  { value: "20%", label: "Avg. CTR Uplift" },
  { value: "2+yrs", label: "Designing & Building" },
];

export default {
  personal,
  services,
  featured,
  projects,
  clients,
  stats,
  categories,
  experience,
};
